import React, { useState, useEffect } from 'react';
import { PlannedItem, PlannedConcept, PlannedMatchGroup, Transaction, Account } from '../../types';
import { updatePlannedItem, deletePlannedItem, projectConceptToPeriod, updatePlannedItemsOrder } from '../../services/planningService';
import { ChevronLeft, ChevronRight, GripVertical, CheckCircle2, Circle, Edit2, Trash2, Save, X, ArrowRight, ListOrdered } from 'lucide-react';
import { format, addMonths, subMonths, parseISO, differenceInMonths, lastDayOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { Reorder, useDragControls } from 'motion/react';

interface CashFlowViewProps {
  period: string; // YYYY-MM
  onChangePeriod: (period: string) => void;
  items: PlannedItem[];
  allItems: PlannedItem[];
  concepts: PlannedConcept[];
  matches: PlannedMatchGroup[];
  transactions: Transaction[];
  accounts: Account[];
  userId: string;
}

const DraggableItem = ({ 
  item, 
  matches, 
  transactions, 
  userId, 
  localItems, 
  editingId, 
  editTitle, 
  setEditTitle, 
  editDate, 
  setEditDate, 
  editAmount, 
  setEditAmount, 
  handleSaveEdit, 
  handleCancelEdit, 
  handleToggleStatus, 
  handleEditClick, 
  handlePostpone,
  deletingId, 
  handleDeleteClick, 
  setDeletingId, 
  handleDragEnd, 
  getActualAmount 
}: any) => {
  const controls = useDragControls();
  const actualAmount = getActualAmount(item);
  const isMatched = matches.some((m: any) => m.plannedItemIds.includes(item.id));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  return (
    <Reorder.Item 
      value={item} 
      as="div"
      dragListener={false}
      dragControls={controls}
      onDragEnd={handleDragEnd}
      className={`p-4 group transition-colors hover:bg-zinc-800/50 relative bg-zinc-900 ${item.status === 'COMPLETED' ? 'opacity-60' : ''}`}
    >
      {editingId === item.id ? (
        <div className="flex flex-col md:flex-row gap-3 w-full bg-zinc-900 p-3 rounded-xl border border-zinc-700">
          <input 
            type="text"
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white flex-1 text-sm outline-none focus:border-indigo-500 transition-colors"
            placeholder="Título"
          />
          <input 
            type="date"
            value={editDate}
            onChange={e => setEditDate(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white w-full md:w-40 text-sm outline-none focus:border-indigo-500 transition-colors"
          />
          <input 
            type="number"
            value={editAmount}
            onChange={e => setEditAmount(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white w-full md:w-32 text-sm outline-none focus:border-indigo-500 transition-colors"
            placeholder="Monto"
          />
          <div className="flex gap-2 items-center justify-end border-t border-zinc-800 md:border-0 pt-3 md:pt-0">
            <button onClick={handleSaveEdit} className="p-2 bg-indigo-500 hover:bg-indigo-600 text-black rounded-lg transition-colors">
              <Save size={16} />
            </button>
            <button onClick={handleCancelEdit} className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded-lg transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start md:items-center gap-4">
            <div 
              onPointerDown={(e) => controls.start(e)}
              className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-zinc-400 opacity-100 lg:[@media(hover:hover)]:opacity-0 lg:group-hover:opacity-100 transition-opacity mt-1 md:mt-0 p-1 bg-zinc-800/50 rounded flex items-center justify-center touch-none"
            >
              <GripVertical size={16} />
            </div>
            <button onClick={() => handleToggleStatus(item)} className="text-zinc-500 hover:text-white transition-colors mt-1 md:mt-0">
              {item.status === 'COMPLETED' ? <CheckCircle2 className="text-emerald-500" /> : <Circle />}
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className={`font-medium ${item.status === 'COMPLETED' ? 'line-through text-zinc-500' : 'text-white'}`}>
                  {item.title}
                </h4>
                {isMatched && item.status === 'COMPLETED' && <span className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase">Matcheado</span>}
                {isMatched && item.status === 'PENDING' && <span className="bg-sky-500/20 text-sky-400 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase">Parcial</span>}
              </div>
              <p className="text-xs text-zinc-500 mt-0.5">{item.date}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between md:justify-end gap-6 pl-10 md:pl-0">
            <div className="text-left md:text-right">
              <p className={`font-bold ${item.type === 'INCOME' ? 'text-emerald-400' : 'text-red-400'} ${item.status === 'COMPLETED' ? 'line-through' : ''}`}>
                {formatCurrency(actualAmount)}
              </p>
              {actualAmount !== item.amount && (
                <p className="text-[10px] text-zinc-500">Plan: {formatCurrency(item.amount)}</p>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              {/* Edit & Delete actions */}
              <div className="flex items-center gap-2 opacity-100 lg:[@media(hover:hover)]:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                <button onClick={() => handlePostpone(item)} className="p-1.5 text-sky-400 hover:bg-sky-400/10 rounded transition-colors" title="Posponer al próximo mes">
                  <ArrowRight size={14} />
                </button>
                <button onClick={() => handleEditClick(item)} className="p-1.5 text-indigo-400 hover:bg-indigo-400/10 rounded transition-colors" title="Editar">
                  <Edit2 size={14} />
                </button>
                
                {deletingId === item.id ? (
                  <div className="flex items-center gap-1 bg-red-500/10 rounded overflow-hidden">
                    <button onClick={() => handleDeleteClick(item.id)} className="px-2 py-1 bg-red-500 text-black text-[10px] font-bold">
                      ¿Seguro?
                    </button>
                    <button onClick={() => setDeletingId(null)} className="px-2 py-1 text-zinc-400 hover:text-white">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => handleDeleteClick(item.id)} className="p-1.5 text-red-400 hover:bg-red-400/10 rounded transition-colors" title="Eliminar">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Reorder.Item>
  );
};

export const CashFlowView: React.FC<CashFlowViewProps> = ({ period, onChangePeriod, items, allItems, concepts, matches, transactions, accounts, userId }) => {
  const [projecting, setProjecting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editAmount, setEditAmount] = useState('');

  const [showCompleted, setShowCompleted] = useState(true);
  const [showPending, setShowPending] = useState(true);
  const [showAccumulative, setShowAccumulative] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Basic sorting for initial state and props updates
  const sortedItems = [...items].sort((a, b) => {
    // 1. Ordenar por fecha (Prioridad Máxima)
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    // 2. Respetar orden manual dentro de la misma fecha
    if ((a.orderIndex ?? 0) !== (b.orderIndex ?? 0)) return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
    // 3. Luego ingresos arriba y egresos abajo
    if (a.type !== b.type) return a.type === 'INCOME' ? -1 : 1;
    return 0;
  });

  const [localItems, setLocalItems] = useState(sortedItems);

  useEffect(() => {
    setLocalItems(sortedItems);
  }, [items]); // Re-run when items change

  const handleSmartSort = async () => {
    const isAccum = (item: PlannedItem) => concepts.find(c => c.id === item.conceptId)?.isAccumulative || false;

    // Preservar el orden actual manual que tengan dentro de sus respectivos grupos
    const sourceList = [...localItems];

    // Group A: Liquidados/Completados && No Acumulativos
    const groupA = sourceList.filter(item => item.status === 'COMPLETED' && !isAccum(item));
    
    // Group B: Pendientes && No Acumulativos
    const groupB = sourceList.filter(item => item.status === 'PENDING' && !isAccum(item))
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        if ((a.orderIndex ?? 0) !== (b.orderIndex ?? 0)) return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
        return a.type === 'INCOME' ? -1 : 1;
      });
    
    // Group C: Acumulativos (estén como estén)
    const groupC = sourceList.filter(item => isAccum(item))
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        if ((a.orderIndex ?? 0) !== (b.orderIndex ?? 0)) return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
        return a.type === 'INCOME' ? -1 : 1;
      });

    const newlySorted = [...groupA, ...groupB, ...groupC];

    const updates = newlySorted.map((item, index) => ({
      id: item.id,
      orderIndex: index
    }));

    // Detectar si hubo cambios reales para evitar writes
    const changed = updates.some(u => {
      const original = items.find(i => i.id === u.id);
      return original && original.orderIndex !== u.orderIndex;
    });

    if (changed) {
      await updatePlannedItemsOrder(userId, updates);
      setToast('Orden inteligente aplicado');
    } else {
      setToast('La lista ya está ordenada');
    }
  };

  const handleDragEnd = async () => {
    const updates = localItems.map((item, index) => ({
      id: item.id,
      orderIndex: index
    }));
    
    const changed = updates.some(u => {
      const original = items.find(i => i.id === u.id);
      return original && original.orderIndex !== u.orderIndex;
    });
    
    if (changed) {
      await updatePlannedItemsOrder(userId, updates);
      setToast('Nuevo orden guardado');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const handlePrevMonth = () => {
    const d = new Date(period + '-01T12:00:00');
    onChangePeriod(format(subMonths(d, 1), 'yyyy-MM'));
  };

  const handleNextMonth = () => {
    const d = new Date(period + '-01T12:00:00');
    onChangePeriod(format(addMonths(d, 1), 'yyyy-MM'));
  };

  const projectCurrentMonth = async () => {
    setProjecting(true);
    let newItemsToCreate: any[] = [];
    
    for (const concept of concepts) {
      // Si la recurrencia es 'NONE', solo proyectamos si no existe ya ningún ítem proyectado para este concepto en todo el historial
      if (concept.recurrence === 'NONE') {
        const alreadyExists = allItems.some(i => i.conceptId === concept.id);
        if (alreadyExists) continue;
      }

      // Start Period Validation
      if (concept.startPeriod) {
        // Don't project if current period is before the start period
        if (period < concept.startPeriod) continue;

        // Validating cadency based on difference in months
        const isValidStart = /^\d{4}-\d{2}$/.test(concept.startPeriod);
        const isValidPeriod = /^\d{4}-\d{2}$/.test(period);
        let shouldProject = true;

        if (isValidStart && isValidPeriod) {
          const startD = parseISO(`${concept.startPeriod}-01T00:00:00`);
          const currentD = parseISO(`${period}-01T00:00:00`);
          const diff = differenceInMonths(currentD, startD);

          if (concept.recurrence === 'BIMONTHLY' && diff % 2 !== 0) shouldProject = false;
          if (concept.recurrence === 'QUARTERLY' && diff % 3 !== 0) shouldProject = false;
          if (concept.recurrence === 'SEMIANNUAL' && diff % 6 !== 0) shouldProject = false;
          if (concept.recurrence === 'ANNUAL' && diff % 12 !== 0) shouldProject = false;
        }

        if (!shouldProject) continue;
      }

      // Check maximum occurrences globally
      if (concept.maxOccurrences && concept.maxOccurrences > 0) {
        const globalCount = allItems.filter(i => i.conceptId === concept.id).length;
        if (globalCount >= concept.maxOccurrences) {
          continue; // Skip this concept if it reached its maximum duration/occurrences
        }
      }

      const exists = items.some(i => i.conceptId === concept.id);
      if (!exists) {
        let projectedDateStr = `${period}-01`;
        if (concept.defaultDay) {
          try {
            const inputDate = new Date(Number(period.split('-')[0]), Number(period.split('-')[1]) - 1, concept.defaultDay, 12, 0, 0);
            if (inputDate.getMonth() !== Number(period.split('-')[1]) - 1) {
              const lastDay = lastDayOfMonth(new Date(Number(period.split('-')[0]), Number(period.split('-')[1]) - 1, 1));
              projectedDateStr = format(lastDay, 'yyyy-MM-dd');
            } else {
              projectedDateStr = format(inputDate, 'yyyy-MM-dd');
            }
          } catch(e) {
            projectedDateStr = format(lastDayOfMonth(parseISO(`${period}-01T12:00:00`)), 'yyyy-MM-dd');
          }
        } else {
           projectedDateStr = format(lastDayOfMonth(parseISO(`${period}-01T12:00:00`)), 'yyyy-MM-dd');
        }

        newItemsToCreate.push({
          concept,
          projectedDateStr
        });
      }
    }

    // Assign temporary order indexes globally by combining old + new and sorting
    if (newItemsToCreate.length > 0) {
       // Just sort `newItemsToCreate` by Date, then by Type
       newItemsToCreate.sort((a, b) => {
          if (a.projectedDateStr !== b.projectedDateStr) return a.projectedDateStr.localeCompare(b.projectedDateStr);
          return a.concept.type === 'INCOME' ? -1 : 1;
       });

       let nextIndex = items.length;
       for (const newItem of newItemsToCreate) {
          await projectConceptToPeriod(userId, newItem.concept, period, newItem.projectedDateStr, nextIndex++);
       }
       setToast('Proyección completada exitosamente');
    } else {
       setToast('No hay nuevos conceptos para proyectar');
    }

    setProjecting(false);
  };

  const handleToggleStatus = async (item: PlannedItem) => {
    const newStatus = item.status === 'PENDING' ? 'COMPLETED' : 'PENDING';
    await updatePlannedItem(userId, item.id, { status: newStatus });
  };

  const handleEditClick = (item: PlannedItem) => {
    setEditingId(item.id);
    setEditTitle(item.title);
    setEditDate(item.date);
    setEditAmount(item.amount.toString());
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const newPeriod = editDate.substring(0, 7); // Extract YYYY-MM
    await updatePlannedItem(userId, editingId, {
      title: editTitle,
      date: editDate,
      period: newPeriod,
      amount: Number(editAmount) || 0
    });
    setEditingId(null);
  };

  const handlePostpone = async (item: PlannedItem) => {
    const currentDate = parseISO(`${item.date}T12:00:00`);
    const newDate = addMonths(currentDate, 1);
    const newDateStr = format(newDate, 'yyyy-MM-dd');
    const newPeriodStr = format(newDate, 'yyyy-MM');
    
    await updatePlannedItem(userId, item.id, {
      date: newDateStr,
      period: newPeriodStr
    });
  };

  const handleDeleteClick = async (itemId: string) => {
    if (deletingId === itemId) {
      await deletePlannedItem(userId, itemId);
      setDeletingId(null);
    } else {
      setDeletingId(itemId);
      setTimeout(() => setDeletingId(null), 3000);
    }
  };

  // Cashflow calc
  const getActualAmount = (item: PlannedItem) => {
    // Si tiene match, sumar los montos reales de las transacciones correspondientes
    const matchGroups = matches.filter(m => m.plannedItemIds.includes(item.id));
    if (matchGroups.length > 0) {
      // Find transactions
      const txIds = new Set(matchGroups.flatMap(m => m.transactionIds));
      let sum = 0;
      transactions.forEach(tx => {
        if (txIds.has(tx.id)) {
          // Calculamos el monto real de la transacción (una sola cara)
          const credits = tx.details.filter(d => d.type === 'CREDIT').reduce((s, d) => s + d.amount, 0);
          const debits = tx.details.filter(d => d.type === 'DEBIT').reduce((s, d) => s + d.amount, 0);
          sum += Math.max(credits, debits);
        }
      });
      if (sum > 0) return sum;
    }
    return item.amount;
  };

  const totalIncomes = items.filter(i => i.type === 'INCOME').reduce((acc, i) => acc + getActualAmount(i), 0);
  const totalExpenses = items.filter(i => i.type === 'EXPENSE').reduce((acc, i) => acc + getActualAmount(i), 0);
  const balance = totalIncomes - totalExpenses;

  // Real Liquidity Projection
  const currentActualBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);

  const pendingIncomes = items.filter(i => i.type === 'INCOME' && i.status === 'PENDING').reduce((acc, i) => {
    let matchedSoFar = 0;
    const matchGroups = matches.filter(m => m.plannedItemIds.includes(i.id));
    matchGroups.forEach(m => {
      m.transactionIds.forEach(tid => {
        const t = transactions.find(tr => tr.id === tid);
        if (t) {
          const credits = t.details.filter(d => d.type === 'CREDIT').reduce((s, d) => s + d.amount, 0);
          const debits = t.details.filter(d => d.type === 'DEBIT').reduce((s, d) => s + d.amount, 0);
          matchedSoFar += Math.max(credits, debits);
        }
      });
    });

    // Validamos que i.amount exista y sea un número, si no es 0
    const itemAmount = typeof i.amount === 'number' && !isNaN(i.amount) ? i.amount : 0;
    const pendingAmount = Math.max(0, itemAmount - matchedSoFar);
    return acc + pendingAmount;
  }, 0);

  const pendingExpenses = items.filter(i => i.type === 'EXPENSE' && i.status === 'PENDING').reduce((acc, i) => {
    let matchedSoFar = 0;
    const matchGroups = matches.filter(m => m.plannedItemIds.includes(i.id));
    matchGroups.forEach(m => {
      m.transactionIds.forEach(tid => {
        const t = transactions.find(tr => tr.id === tid);
        if (t) {
          const credits = t.details.filter(d => d.type === 'CREDIT').reduce((s, d) => s + d.amount, 0);
          const debits = t.details.filter(d => d.type === 'DEBIT').reduce((s, d) => s + d.amount, 0);
          matchedSoFar += Math.max(credits, debits);
        }
      });
    });

    // Validamos que i.amount exista y sea un número, si no es 0
    const itemAmount = typeof i.amount === 'number' && !isNaN(i.amount) ? i.amount : 0;
    const pendingAmount = Math.max(0, itemAmount - matchedSoFar);
    return acc + pendingAmount;
  }, 0);

  const projectedEndingBalance = currentActualBalance + pendingIncomes - pendingExpenses;

  const [viewMode, setViewMode] = useState<'MONTH_ISOLATED' | 'REAL_LIQUIDITY'>('MONTH_ISOLATED');

  return (
    <div className="space-y-6 relative">
      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-8 right-8 z-[100] pointer-events-none">
          <div className="bg-zinc-800 border border-zinc-700 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">{toast}</span>
          </div>
        </div>
      )}

      {/* Sticky Header Section */}
      <div className="sticky top-0 z-40 bg-zinc-950/80 backdrop-blur-md pt-2 pb-4 -mx-4 px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-xl p-1 shadow-lg">
            <button onClick={handlePrevMonth} className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400 transition-colors">
              <ChevronLeft size={20} />
            </button>
            <div className="w-40 text-center font-bold text-lg text-indigo-400 capitalize">
              {format(new Date(period + '-01T12:00:00'), 'MMMM yyyy', { locale: es })}
            </div>
            <button onClick={handleNextMonth} className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400 transition-colors">
              <ChevronRight size={20} />
            </button>
          </div>

          <button 
            onClick={projectCurrentMonth}
            disabled={projecting}
            className="w-full md:w-auto bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/20 px-6 py-2.5 rounded-xl text-sm font-bold transition-all shadow-lg active:scale-95 disabled:opacity-50"
          >
            {projecting ? 'Proyectando...' : 'Proyectar Conceptos Periódicos'}
          </button>
        </div>
      </div>

      <div className="bg-zinc-900/50 p-1 rounded-xl border border-zinc-800 flex gap-1 w-full md:w-fit mb-4">
        <button
          onClick={() => setViewMode('MONTH_ISOLATED')}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${viewMode === 'MONTH_ISOLATED' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
        >
          Mes Aislado
        </button>
        <button
          onClick={() => setViewMode('REAL_LIQUIDITY')}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${viewMode === 'REAL_LIQUIDITY' ? 'bg-indigo-500 text-black' : 'text-zinc-500 hover:text-zinc-300'}`}
        >
          Proyección Real
        </button>
      </div>

      {viewMode === 'MONTH_ISOLATED' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4">
            <h3 className="text-emerald-400 text-xs font-bold mb-1">Ingresos Planificados</h3>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(totalIncomes)}</p>
          </div>
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4">
            <h3 className="text-red-400 text-xs font-bold mb-1">Egresos Planificados</h3>
            <p className="text-2xl font-bold text-red-400">{formatCurrency(totalExpenses)}</p>
          </div>
          <div className={`border rounded-2xl p-4 ${balance >= 0 ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
            <h3 className={`text-xs font-bold mb-1 ${balance >= 0 ? 'text-indigo-400' : 'text-red-400'}`}>Balance del Mes</h3>
            <p className={`text-2xl font-bold ${balance >= 0 ? 'text-indigo-400' : 'text-red-400'}`}>
               {balance > 0 ? '+' : ''}{formatCurrency(balance)}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-4">
            <h3 className="text-zinc-400 text-xs font-bold mb-1">Saldo Actual Cuentas</h3>
            <p className="text-xl font-bold text-white">{formatCurrency(currentActualBalance)}</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4">
            <h3 className="text-emerald-400 text-xs font-bold mb-1">Cobros Pendientes</h3>
            <p className="text-xl font-bold text-emerald-400">+{formatCurrency(pendingIncomes)}</p>
          </div>
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4">
            <h3 className="text-red-400 text-xs font-bold mb-1">Pagos Pendientes</h3>
            <p className="text-xl font-bold text-red-400">-{formatCurrency(pendingExpenses)}</p>
          </div>
          <div className={`border rounded-2xl p-4 ${projectedEndingBalance >= 0 ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
            <h3 className={`text-xs font-bold mb-1 ${projectedEndingBalance >= 0 ? 'text-indigo-400' : 'text-red-400'}`}>Proyección Fin de Mes</h3>
            <p className={`text-xl font-bold ${projectedEndingBalance >= 0 ? 'text-indigo-400' : 'text-red-400'}`}>
               {formatCurrency(projectedEndingBalance)}
            </p>
          </div>
        </div>
      )}

      <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-2xl overflow-hidden mt-6">
        <div className="sticky top-[84px] z-30 bg-zinc-900 border-b border-zinc-700/50 p-4 shadow-md">
          <div className="flex flex-wrap gap-4 justify-between items-center text-xs font-bold text-zinc-400 tracking-widest">
            <div className="flex gap-4 items-center overflow-x-auto pb-1 slim-scrollbar no-scrollbar">
              <button 
                onClick={() => setShowPending(!showPending)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all border ${showPending ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400' : 'bg-transparent border-zinc-800 text-zinc-500 opacity-60'}`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${showPending ? 'bg-indigo-500' : 'bg-zinc-700'}`} />
                Pendientes
              </button>
              <button 
                onClick={() => setShowCompleted(!showCompleted)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all border ${showCompleted ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-transparent border-zinc-800 text-zinc-500 opacity-60'}`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${showCompleted ? 'bg-emerald-500' : 'bg-zinc-700'}`} />
                Completados
              </button>
              <button 
                onClick={() => setShowAccumulative(!showAccumulative)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all border ${showAccumulative ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-transparent border-zinc-800 text-zinc-500 opacity-60'}`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${showAccumulative ? 'bg-amber-500' : 'bg-zinc-700'}`} />
                Acumulativos
              </button>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={handleSmartSort}
                className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-3 py-1.5 rounded-lg transition-colors border border-zinc-700/50 normal-case tracking-normal"
                title="Agrupar: Liquidados, Pendientes y Acumulativos"
              >
                <ListOrdered size={14} />
                <span className="hidden lg:inline">Auto-ordenar</span>
              </button>
            </div>
          </div>
          
          <div className="hidden md:grid grid-cols-[1fr_auto] gap-4 mt-4 px-4 text-[10px] uppercase text-zinc-500 tracking-tighter font-black">
            <div className="flex items-center gap-12">
              <span className="w-6 shrink-0 text-center">#</span>
              <span className="ml-6">Título / Concepto</span>
            </div>
            <div className="flex items-center gap-12 mr-2">
              <span className="w-24 text-right">Importe</span>
              <span className="w-16 text-center">Acciones</span>
            </div>
          </div>
        </div>

        <Reorder.Group axis="y" values={localItems} onReorder={setLocalItems} as="div" className="divide-y divide-zinc-800">
          {localItems.filter(item => {
            const isAccum = concepts.find(c => c.id === item.conceptId)?.isAccumulative || false;
            if (!showCompleted && item.status === 'COMPLETED') return false;
            if (!showPending && item.status === 'PENDING') return false;
            if (!showAccumulative && isAccum) return false;
            return true;
          }).map(item => (
            <DraggableItem
              key={item.id}
              item={item}
              matches={matches}
              transactions={transactions}
              userId={userId}
              localItems={localItems}
              editingId={editingId}
              editTitle={editTitle}
              setEditTitle={setEditTitle}
              editDate={editDate}
              setEditDate={setEditDate}
              editAmount={editAmount}
              setEditAmount={setEditAmount}
              handleSaveEdit={handleSaveEdit}
              handleCancelEdit={handleCancelEdit}
              handleToggleStatus={handleToggleStatus}
              handleEditClick={handleEditClick}
              handlePostpone={handlePostpone}
              deletingId={deletingId}
              handleDeleteClick={handleDeleteClick}
              setDeletingId={setDeletingId}
              handleDragEnd={handleDragEnd}
              getActualAmount={getActualAmount}
            />
          ))}
          {localItems.length === 0 && (
            <div className="p-8 text-center text-zinc-500">
              No hay ítems planificados para este mes.
            </div>
          )}
        </Reorder.Group>
      </div>
    </div>
  );
};
